import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { ArrowLeft, Trash2, Lock, Globe, Share2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { resolveAssetUrl } from "@/lib/assetUrl";
import FollowButton from "@/components/FollowButton";
import { toast } from "sonner";

/**
 * Collection detail — shows every Find kept to the Collection.
 * Owners see a delete affordance + can remove Finds. Public Collections
 * inject Open Graph meta tags so social shares render our branded card.
 */

// Managed OG/meta tags — we upsert them into <head> when the board is public
// so a paste into iMessage/Twitter/Slack fetches the branded share card.
function upsertMeta(attr, key, value) {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function removeMeta(attr, key) {
  const el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (el) el.remove();
}

export default function BoardDetailPage() {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/blinterest/boards/${boardId}`, { withCredentials: true })
      .then((r) => setBoard(r.data))
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Couldn't load Collection");
        setBoard(null);
      })
      .finally(() => setLoading(false));
  }, [boardId]);

  // Inject / refresh OG tags whenever we load a public Collection.
  useEffect(() => {
    if (!board || board.privacy !== "public") return;
    const title = `${board.name} · Blinterest by The BLK Community`;
    const pinCount = board.pin_count || (board.pins || []).length || 0;
    const desc =
      board.description ||
      `${pinCount} Find${pinCount === 1 ? "" : "s"} kept on Blinterest by The BLK Community.`;
    const shareUrl = `${API}/blinterest/boards/${board.board_id}/share`;
    const ogImage = `${API}/blinterest/boards/${board.board_id}/og-image.jpg`;
    document.title = title;
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "Blinterest by The BLK Community");
    upsertMeta("property", "og:url", shareUrl);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", desc);
    upsertMeta("name", "twitter:image", ogImage);
    return () => {
      // Clean up so other pages don't inherit a stale card
      ["og:type", "og:site_name", "og:url", "og:title", "og:description",
       "og:image", "og:image:width", "og:image:height"].forEach((k) =>
        removeMeta("property", k),
      );
      ["twitter:card", "twitter:title", "twitter:description", "twitter:image"]
        .forEach((k) => removeMeta("name", k));
    };
  }, [board]);

  const removePin = async (pinId) => {
    if (!board) return;
    try {
      await axios.delete(`${API}/blinterest/boards/${board.board_id}/pins/${pinId}`, {
        withCredentials: true,
      });
      setBoard((prev) => ({
        ...prev,
        pins: prev.pins.filter((p) => p.pin_id !== pinId),
        pin_count: (prev.pin_count || 0) - 1,
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't remove Find");
    }
  };

  const deleteBoard = async () => {
    if (!board) return;
    if (!window.confirm(`Delete "${board.name}" and all its Finds?`)) return;
    try {
      await axios.delete(`${API}/blinterest/boards/${board.board_id}`, {
        withCredentials: true,
      });
      toast.success("Collection deleted");
      navigate("/blinterest/boards");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete Collection");
    }
  };

  const shareBoard = async () => {
    if (!board) return;
    if (board.privacy !== "public") {
      toast.info("Make this Collection public to share it");
      return;
    }
    const shareUrl = `${API}/blinterest/boards/${board.board_id}/share`;
    const shareData = {
      title: `${board.name} · Blinterest`,
      text: `Check out my "${board.name}" Collection on Blinterest.`,
      url: shareUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch {
        /* user cancelled — fall through to clipboard */
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied to clipboard");
    } catch {
      toast.error("Couldn't copy share link");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-tbc-navy">
        <Navbar />
        <div className="max-w-6xl mx-auto px-4 py-10 animate-pulse">
          <div className="h-8 bg-white/10 rounded w-1/3 mb-4" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="aspect-square bg-white/10 rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="min-h-screen bg-tbc-navy">
        <Navbar />
        <div className="max-w-6xl mx-auto px-4 py-16 text-center text-white">
          <h1 className="font-heading text-3xl mb-4">Collection not found</h1>
          <Link to="/blinterest">
            <Button className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full">
              Back to Discover
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const isOwner = user && board.user_id === user.user_id;
  const pinCount = (board.pins || []).length;
  const isPublic = board.privacy === "public";

  return (
    <div className="min-h-screen bg-tbc-navy" data-testid="board-detail-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/blinterest/boards"
          className="inline-flex items-center text-white/70 hover:text-white font-body text-sm mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to My Collections
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl text-white flex items-center gap-2">
              {board.name}
              {board.privacy === "private" ? (
                <Lock className="w-5 h-5 text-white/40" />
              ) : (
                <Globe className="w-5 h-5 text-white/40" />
              )}
            </h1>
            {board.description && (
              <p className="text-white/60 font-body text-sm mt-1">{board.description}</p>
            )}
            <p className="text-white/50 font-body text-xs mt-1">
              {pinCount} Find{pinCount === 1 ? "" : "s"}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isPublic && (
              <Button
                onClick={shareBoard}
                variant="outline"
                className="border-white/30 text-white bg-transparent hover:bg-white/10 rounded-full"
                data-testid="board-share-btn"
              >
                <Share2 className="w-4 h-4 mr-1" />
                Share
              </Button>
            )}
            {!isOwner && isPublic && (
              <FollowButton
                targetType="board"
                targetId={board.board_id}
                variant="default"
                showCount
              />
            )}
            {isOwner && (
              <Button
                onClick={deleteBoard}
                variant="outline"
                className="border-red-400/40 text-red-400 bg-transparent hover:bg-red-500/10 rounded-full"
                data-testid="delete-board-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete Collection
              </Button>
            )}
          </div>
        </div>

        {pinCount === 0 ? (
          <div className="text-center py-16 text-white/60">
            <p className="font-heading text-2xl text-white mb-2">Empty Collection</p>
            <p className="font-body text-sm mb-6">
              Start keeping Finds from Discover to fill this up.
            </p>
            <Link to="/blinterest">
              <Button className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full">
                Start Discovering
              </Button>
            </Link>
          </div>
        ) : (
          <div className="columns-2 md:columns-3 lg:columns-4 gap-4">
            {board.pins.map((pin) => (
              <div
                key={pin.pin_id}
                className="mb-4 break-inside-avoid rounded-2xl overflow-hidden bg-white/5 border border-white/5 group relative"
                data-testid={`board-pin-${pin.pin_id}`}
              >
                <Link to={`/blinterest/pin/${pin.pin_id}`}>
                  <img
                    src={resolveAssetUrl(pin.image_url)}
                    alt={pin.title}
                    className="w-full h-auto object-cover"
                    loading="lazy"
                  />
                  <div className="p-3">
                    <p className="text-white font-body text-sm line-clamp-2">{pin.title}</p>
                  </div>
                </Link>
                {isOwner && (
                  <button
                    type="button"
                    onClick={() => removePin(pin.pin_id)}
                    className="absolute top-3 right-3 w-8 h-8 rounded-full bg-red-500/80 hover:bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`board-pin-remove-${pin.pin_id}`}
                    aria-label="Remove Find"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
